import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReportHeader from "./ReportHeader";
import SummaryComponent from "./SummaryComponent";
import FailedTestDetailsTab from "./FailedTestDetailsTab";
import AgenticAIDosReportTab from "./AgenticAIDos/AgenticAIDosReportTab";
import McpServerTestsReportTab from "./McpServerTests/McpServerTestsReportTab";
import AiModelAttackSurfaceRecs from "./AiModelAttackSurface/AiModelAttackSurfaceRecs";
import RtoReportTab from "./Rto/RtoReportTab";
import PromptsDocsRecs from "./PromptsDocs/PromptsDocsRecs";
import RegulationComplianceRecs from "./RegulationCompliance/RegulationComplianceRecs";
import { categoryLabels, getVisibleTabsForProfileType } from "../../utils/categoryLabels";
import { downloadPdfReport } from "../../utils/downloadPdfReport";
import { isConsolidatedReport } from "../../utils/apiHelper";
import "../../styles/ReportHistory.css";
import "../../styles/ReportDashboard.css";

// Modal component for showing detailed report
const ReportModal = ({ isOpen, onClose, reportData, navigate }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_common" });
  const [activeTab, setActiveTab] = useState("summary");
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  // Get profile type and determine visible tabs (before early return)
  // console.log('reportData in ReportModal: ' + JSON.stringify(reportData));
  const visibleTabs = getVisibleTabsForProfileType(reportData);

  // Ensure activeTab is valid for current profile type (hooks must be before early return)
  useEffect(() => {
    if (reportData && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] || "summary");
    }
  }, [reportData, visibleTabs, activeTab]);

  if (!isOpen || !reportData) return null;

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return t("na");
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return t("invalidDate");
    }
  };

  // Build view data directly from score_json without recalculation (matching latest report page)
  const scoreJson = reportData?.score_json || {};
  const categoriesData = scoreJson.data || {};

  // Helper to get category key from display label
  const getCategoryKey = (displayLabel) => {
    return Object.keys(categoryLabels).find(key => categoryLabels[key] === displayLabel) || null;
  };
  // Ensure all 7 categories appear even if missing in API response
  const data = Object.keys(categoryLabels).map((categoryKey) => {
    const c = categoriesData[categoryKey] || {};
    const readiness = typeof c.readiness === "number" ? Math.round(c.readiness) : 0;
    const label = categoryLabels[categoryKey] || categoryKey;
    return {
      category: label,
      totalScans: c.total_tests || 0,
      scanPassed: c.total_passed || 0,
      readiness,
      failed_tests: c.failed_tests || {},
      sub_cat_readiness: c.sub_cat_readiness || {},
      avg_response_time: c.avg_response_time || 0,
      sub_cat_avg_time: c.sub_cat_avg_time || {}
    };
  });

  // Function to calculate readiness percentage and risk level (matching latest report page)
  const calculateSummaryData = (data) => {
    const categoryMap = new Map();
    
    // Group data by category and calculate totals
    data.forEach(item => {
      const category = item.category;
      const scanPassed = item.scanPassed;
      const totalScans = item.totalScans;
      
      if (!categoryMap.has(category)) {
        categoryMap.set(category, { totalPassed: 0, totalScans: 0 });
      }
      
      const categoryData = categoryMap.get(category);
      categoryData.totalPassed += scanPassed;
      categoryData.totalScans += totalScans;
    });
    
    // Convert to summary data format
    const summaryData = [];
    categoryMap.forEach((categoryData, category) => {
      const readiness = categoryData.totalScans > 0 
        ? Math.round((categoryData.totalPassed / categoryData.totalScans) * 100)
        : 0;
      
      summaryData.push({
        solution: category,
        readiness: readiness,
        totalTests: categoryData.totalScans,
        passedTests: categoryData.totalPassed
      });
    });
    
    return summaryData;
  };

  const summaryData = calculateSummaryData(data);

  // Always use top-level overall_readiness from the detailed API response
  const overallReadiness = typeof reportData?.overall_readiness === "number"
    ? reportData.overall_readiness
    : 0;

  const donutData = data
    .filter(c => {
      const key = getCategoryKey(c.category);
      return key && categoriesData[key];
    })
    .map((c, i) => ({
      label: c.category,
      value: c.readiness,
      color: [
        "#ab2c18", "#c75619", "#daa520", "#80a000", "#008000", "#145d3d"
      ][i % 6],
    }));

  const stats = {
    totalUsers: 69,
    totalGroups: 13,
    totalCritical: 0,
    phishingEmailsSent: 134,
    emailsVisited: 3,
    tutorialsTaken: 94,
    quizTaken: 65,
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{reportData.report_name || t("reportDetails")}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              onClick={async () => {
                if (isDownloadingPDF) return;
                setIsDownloadingPDF(true);
                try {
                  await downloadPdfReport({
                    reportId: reportData?.id,
                    profileType: reportData?.profile_details_summary?.profile_type,
                    consolidation: isConsolidatedReport(reportData),
                  });
                } catch (err) {
                  console.error("Error downloading PDF:", err);
                  alert(err.message || t("failedToDownloadPdf"));
                } finally {
                  setIsDownloadingPDF(false);
                }
              }}
              className="btn btn-primary"
              style={{
                fontSize: "14px",
                padding: "6px 12px",
                opacity: isDownloadingPDF ? 0.6 : 1,
                cursor: isDownloadingPDF ? "not-allowed" : "pointer",
              }}
              disabled={isDownloadingPDF}
            >
              {isDownloadingPDF ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                  {t("generatingPdf")}
                </>
              ) : (
                t("downloadPdfReport")
              )}
            </button>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
        </div>
        
        <div className="modal-body">
          <div className="dashboard">
            {/* Report Header */}
            <ReportHeader
              company={reportData.user_details_summary?.company_name || "IronSDN"}
              profile={reportData.profile_details_summary?.profile_name}
              user={reportData.user_details_summary?.email || t("na")}
              userId={reportData?.user_id || "N/A"}
              date={formatTimestamp(reportData.created_at)}
              reportId={reportData?.id || "N/A"}
              profileId={reportData?.profile_id || "N/A"}
              profileType={reportData.profile_details_summary?.profile_type || "N/A"}
              targetName={reportData?.target_details_summary?.target_name ?? ""}
              overlapPolicy={reportData?.consolidation_details_summary?.overlap_policy}
            />

            {/* Tabs */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div className="tabs">
                {visibleTabs.includes("summary") && (
                  <button
                    className={activeTab === "summary" ? "active" : ""}
                    onClick={() => setActiveTab("summary")}
                  >
                    {t("summary")}
                  </button>
                )}
                
                {visibleTabs.includes("agentic-dos") && (
                  <button
                    className={activeTab === "agentic-dos" ? "active" : ""}
                    onClick={() => setActiveTab("agentic-dos")}
                  >
                    {t("agenticAiDosReport")}
                  </button>
                )}
                {visibleTabs.includes("mcp-tests") && (
                  <button
                    className={activeTab === "mcp-tests" ? "active" : ""}
                    onClick={() => setActiveTab("mcp-tests")}
                  >
                    {t("mcpServerTestsReport")}
                  </button>
                )}
                {visibleTabs.includes("ai-model-attack-surface") && (
                  <button
                    className={activeTab === "ai-model-attack-surface" ? "active" : ""}
                    onClick={() => setActiveTab("ai-model-attack-surface")}
                  >
                    {t("recommendations")}
                  </button>
                )}
                {visibleTabs.includes("prompts-docs") && (
                  <button
                    className={activeTab === "prompts-docs" ? "active" : ""}
                    onClick={() => setActiveTab("prompts-docs")}
                  >
                    {t("recommendations")}
                  </button>
                )}
                {visibleTabs.includes("rto") && (
                  <button
                    className={activeTab === "rto" ? "active" : ""}
                    onClick={() => setActiveTab("rto")}
                  >
                    {t("responseTimeOptimization")}
                  </button>
                )}
                {visibleTabs.includes("regulation-compliance") && (
                  <button
                    className={activeTab === "regulation-compliance" ? "active" : ""}
                    onClick={() => setActiveTab("regulation-compliance")}
                  >
                    {t("recommendations")}
                  </button>
                )}
                {visibleTabs.includes("recommendations") && (
                  <button
                    className={activeTab === "recommendations" ? "active" : ""}
                    onClick={() => setActiveTab("recommendations")}
                  >
                    {t("recommendations")}
                  </button>
                )}
                {visibleTabs.includes("failed-tests") && (
                  <button
                    className={activeTab === "failed-tests" ? "active" : ""}
                    onClick={() => setActiveTab("failed-tests")}
                  >
                    {t("failedTestDetails")}
                  </button>
                )}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === "summary" && (
              <SummaryComponent 
                summaryData={summaryData}
                overallReadiness={overallReadiness}
                categoryStats={data}
                donutData={donutData}
                stats={stats}
                data={data}
                reportData={reportData}
              />
            )}

            {activeTab === "failed-tests" && (
              <FailedTestDetailsTab rawApiResponse={reportData} />
            )}

            {activeTab === "agentic-dos" && (
              <AgenticAIDosReportTab reportData={reportData} />
            )}

            {activeTab === "mcp-tests" && (
              <McpServerTestsReportTab reportData={reportData} />
            )}

            {activeTab === "ai-model-attack-surface" && (
              <AiModelAttackSurfaceRecs reportData={reportData} />
            )}

            {activeTab === "prompts-docs" && (
              <PromptsDocsRecs reportData={reportData} />
            )}

            {activeTab === "rto" && (
              <RtoReportTab reportData={reportData} />
            )}

            {activeTab === "regulation-compliance" && (
              <RegulationComplianceRecs reportData={reportData} />
            )}

            {activeTab === "recommendations" && (
              <PromptsDocsRecs reportData={reportData} />
            )}

            <div style={{ position: "absolute", left: "-9999px", top: 0, width: "1200px" }}>
              <div id="summary-section" style={{ width: "100%", padding: "24px", backgroundColor: "var(--body-bg)" }}>
                <SummaryComponent
                  summaryData={summaryData} overallReadiness={overallReadiness} categoryStats={data} donutData={donutData} stats={stats} data={data} reportData={reportData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
